import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

// Key material has to be in memory before anything reads it: a device that
// looks at its identity too early sees none and helpfully generates a new one,
// which makes it a stranger to every contact it has.
import { hydrateSecureStore, adoptFromLocalStorage, isEvictable, secureSyncStorage } from './services/secureStore.mjs';
import { refreshConfig, KIND } from './services/relayConfig.mjs';
import { api } from './services/api';

const root = ReactDOM.createRoot(document.getElementById('root'));

hydrateSecureStore().then((backend) => {
  // One-time move of what is already on disk, so today's devices keep their
  // identity and their sessions.
  const legacy = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('voxiox_identity_v1') || k.startsWith('voxiox_tofu_v1')
                || k.startsWith('voxiox_ratchet_v1'))) legacy.push(k);
    }
  } catch (e) { /* private mode */ }
  adoptFromLocalStorage(legacy);
  console.log('[SecureStore] backend:', backend, isEvictable() ? '(evictable)' : '(persistent)');
  // The signed list of paths to CORE (SEC-ARCH-002): fetched unauthenticated,
  // verified against the embedded owner key, kept only if newer. A refusal is
  // news about the server and is logged as such; the held list stays.
  // At boot the network is often not there yet (a phone waking up, a VPN
  // still handshaking): an unreachable CORE is retried a few times with
  // growing gaps, then left to the next boot. Any other answer is final.
  const RETRY_MS = [10000, 60000, 300000];
  const tryConfig = (attempt) => refreshConfig(api, secureSyncStorage, KIND.RELAY_LIST)
    .then((r) => {
      console.log('[config] relay-list:', r.ok ? `version ${r.version}${r.adopted ? ' adopted' : ''}` : `${r.error}${r.detail ? ' — ' + r.detail : ''}`);
      if (!r.ok && r.error === 'unreachable' && attempt < RETRY_MS.length) setTimeout(() => tryConfig(attempt + 1), RETRY_MS[attempt]);
    })
    .catch((e) => console.warn('[config] relay-list: verifier threw', e && e.message));
  tryConfig(0);
}).catch((e) => {
  console.error('[SecureStore] hydration failed:', e);
}).finally(() => {
  root.render(<React.StrictMode><App /></React.StrictMode>);
});

// Ask the browser not to evict us.
//
// Everything that makes a call private lives in this origin's storage: the
// device identity, the pinned peer keys, and — once ADR-011 lands — the
// conversation keys and the local history. Without persistence granted, a
// browser under storage pressure may clear it, and the visible symptom is not
// "settings lost" but "every contact now sees you as a new device", or later,
// a history that cannot be decrypted by anyone including you.
//
// It is a request, not a guarantee: Chromium grants it on an installed PWA or
// an engaged origin, Safari on an added-to-home-screen app. Logged either way,
// because "we asked and were refused" is a fact worth having when someone
// reports losing their keys.
if (navigator.storage?.persist) {
  navigator.storage.persisted?.().then((already) => {
    if (already) { console.log('[Storage] already persistent'); return; }
    return navigator.storage.persist().then((granted) => {
      console.log(granted ? '[Storage] persistence granted' : '[Storage] persistence REFUSED — keys may be evicted');
    });
  }).catch(() => {});
}

// Register service worker for PWA + detect updates.
// Not in the native shells: their UI ships inside the binary (ADR-017), and
// a service worker updating it from the network would be SEC-01 reopened.
if ('serviceWorker' in navigator && !window.__VOXIOX_PLATFORM__ && !window.__VOXIOX_SERVER_URL__) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((reg) => {
        console.log('[SW] Registered:', reg.scope);

        // Detect new SW version
        reg.onupdatefound = () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.onstatechange = () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[SW] New version available — showing update banner');
              window.dispatchEvent(new Event('swUpdate'));
            }
          };
        };

        // Check for updates every 30 seconds
        setInterval(() => { reg.update().catch(() => {}); }, 30 * 1000);
      })
      .catch((err) => console.log('[SW] Registration failed:', err));
  });
}
